import { baseApi } from './baseApi';

export type MarketplaceProductKind =
  | 'credit_pack'
  | 'featured_pack'
  | 'ml_pack'
  | 'ai_studio_plan'
  | 'course_unlock'
  | 'graphic_pack'
  | 'external_service'
  | 'discount_code'
  /** Plan mensual de Phylax. NO se compra acá: el CTA abre el hilo con soporte. */
  | 'phylax_plan'
  /** Entradas de un evento. El precio sale del evento; se compra acá con el diálogo de compra del evento. */
  | 'evento';
export type MarketplacePaymentType = 'credits' | 'money' | 'partner';
/**
 * `pending_approval` y `rejected` son de la compra manual de paquetes de créditos: el agente
 * pide, alguien confirma que la plata entró, y recién ahí se acredita. (21/08/2026)
 */
export type MarketplaceOrderStatus =
  | 'pending' | 'paid' | 'failed' | 'refunded'
  | 'pending_approval' | 'rejected';

export interface MarketplaceProduct {
  _id: string;
  sku: string;
  kind: MarketplaceProductKind;
  name: string;
  description?: string;
  paymentType: MarketplacePaymentType;
  price: number;
  basePrice: number;
  isPromo: boolean;
  promoUntil?: string;
  enabled: boolean;
  displayOrder: number;
  metadata: Record<string, any>;
}

export interface MarketplaceSlots {
  featured: number;
  ml: number;
}

export interface StudioPlanState {
  sku:         string;
  tier:        'free' | 'start' | 'pro';
  credits:     number;
  activatedAt: string;
  renewsAt:    string;
  active:      boolean;
}

export interface ProductsResponse {
  systemEnabled: boolean;
  products: MarketplaceProduct[];
  slots: MarketplaceSlots;
  studioPlan?: StudioPlanState | null;
}

export interface OrderEffects {
  creditsGranted?: number;
  featuredSlotsGranted?: number;
  daysPerFeaturedSlot?: number;
  mlSlotsGranted?: number;
  courseUnlocked?: string;
}

export type MarketplaceOrderPaymentStatus = 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled' | 'expired' | 'refunded';

/** Info parcial del PaymentTransaction asociado a esta orden (solo cuando se pagó con dLocal). */
export interface MarketplaceOrderPaymentInfo {
  status:            MarketplaceOrderPaymentStatus;
  totalAmount:       number;
  feeAmount:         number;
  paymentMethod?:    string;
  providerPaymentId?: string;
  card?: {
    brand?:      string;
    last4?:      string;
    holderName?: string;
  };
}

export interface MarketplaceOrder {
  _id: string;
  /** Cupón aplicado al comprar. */
  descuento?: { codigo: string; pct: number; precioOriginal: number };
  user: string | { _id: string; name: string; lastName?: string; email?: string; avatar?: string };
  productSku: string;
  productName: string;
  productKind: MarketplaceProductKind;
  paymentType: MarketplacePaymentType;
  amount: number;
  currency?: string;
  status: MarketplaceOrderStatus;
  paymentProvider?: 'mercadopago' | 'wallet' | 'dlocal';
  effects: OrderEffects;
  /** Info del pago externo cuando aplica (paymentProvider='dlocal'). */
  payment?: MarketplaceOrderPaymentInfo;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrdersResponse {
  items: MarketplaceOrder[];
  total: number;
}

export interface ApplyFeaturedResponse {
  propertyId: string;
  featuredUntil: string;
  remainingSlots: number;
}

export interface RevenueTotals {
  totalOrders: number;
  revenueUSD: number;
  revenueUYU: number;
  creditsConsumed: number;
  creditsGranted: number;
}

export interface RevenueByMonth {
  _id: { year: number; month: number };
  orders: number;
  revenueUSD: number;
  revenueUYU: number;
  creditsConsumed: number;
}

export interface RevenueByProduct {
  _id: string;
  productName: string;
  productKind: MarketplaceProductKind;
  orders: number;
  revenueUSD: number;
  revenueUYU: number;
  creditsConsumed: number;
}

export interface RevenueResponse {
  totals: RevenueTotals;
  byMonth: RevenueByMonth[];
  byProduct: RevenueByProduct[];
}

export interface AdminProductPayload {
  sku: string;
  kind: MarketplaceProductKind;
  name: string;
  description?: string;
  paymentType: MarketplacePaymentType;
  price: number;
  promoPrice?: number;
  promoFrom?: string;
  promoUntil?: string;
  enabled?: boolean;
  displayOrder?: number;
  metadata?: Record<string, any>;
}

export interface PagoConfigPhylax {
  culqiPublicKey: string;
  metaAppId: string;
  metaWaConfigId: string;
}

export interface ConfiguracionPhylax {
  whatsapp: { conectado: boolean; numero: string; modo: string };
  redes: {
    instagram: { conectado: boolean; cuenta?: string; vencido?: boolean };
    facebook: { conectado: boolean; cuenta?: string; paginaId?: string; paginas?: { id: string; name: string }[] };
  };
  conocimiento: { status: string; sourceUrl: string; etapa: string; mensaje: string; porcentaje: number; borradores: number; errorDetail: string };
}

/** El plan de Phylax del tenant (GET /marketplace/phylax/mi-plan). */
export interface MiPlanPhylax {
  /** Contratar, recargar y configurar son del tenant_admin (o de soporte adentro). */
  puedeAdministrar: boolean;
  /** Lo que cuesta: la suscripción y cada mensaje (se descuenta del saldo). */
  oferta: { nombre: string; precioUsd: number; precioMensajeUsd: number; recargaMinUsd: number };
  plan: {
    key: 'agente';
    nombre: string;
    vigente: boolean;
    hasta: string | null;
    incluidos: number;
    usados: number | null;
    extra: number | null;
    redes: number | null;
    sinAtender: number | null;
    onboardingCompleto: boolean | null;
  } | null;
  saldo: { saldoUsd: number; mensajesQueAlcanzan: number } | null;
}

export const marketplaceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listMarketplaceProducts: build.query<ProductsResponse, { kind?: MarketplaceProductKind } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.kind) p.set('kind', f.kind);
        const qs = p.toString();
        return `/marketplace/products${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: ProductsResponse }) => res.data,
      providesTags: ['MarketplaceProduct', 'MarketplaceSlots'],
    }),

    /** Vista previa de un cupón para este producto: qué % y a cuánto queda. Mismo criterio que la compra. */
    evaluarCupon: build.query<{ codigo: string; pct: number; precioOriginal: number; precioFinal: number }, { code: string; sku: string }>({
      query: ({ code, sku }) => ({ url: `/marketplace/coupons/${encodeURIComponent(code.trim().toUpperCase())}`, params: { sku } }),
      transformResponse: (res: { data: { codigo: string; pct: number; precioOriginal: number; precioFinal: number } }) => res.data,
    }),
    purchaseProduct: build.mutation<MarketplaceOrder, { sku: string; couponCode?: string }>({
      query: (body) => ({ url: '/marketplace/purchase', method: 'POST', body }),
      transformResponse: (res: { data: MarketplaceOrder }) => res.data,
      invalidatesTags: ['MarketplaceOrder', 'MarketplaceSlots', 'Credits', 'Course', 'CourseProgress', 'MarketplaceProduct', 'StudioOverview', 'Wallet'],
    }),

    /**
     * Phylax (Impulso, Alcance, Escala): se paga con el Culqi de PHYLAX, no acá. Estos
     * endpoints devuelven el estado y links a las páginas de Phylax (pago, recarga, panel).
     */
    getMiPlanPhylax: build.query<MiPlanPhylax, void>({
      query: () => '/marketplace/phylax/mi-plan',
      transformResponse: (res: { data: MiPlanPhylax }) => res.data,
      providesTags: ['PhylaxPlan'],
    }),
    /** ¿El tenant tiene Phylax? Imperium siempre; Booster, con un plan vigente. */
    getTienePhylax: build.query<{ tiene: boolean }, void>({
      query: () => '/marketplace/phylax/tiene',
      transformResponse: (res: { data: { tiene: boolean } }) => res.data,
      providesTags: ['PhylaxPlan'],
    }),
    /** Claves públicas de Culqi (la cuenta de Phylax) y de la app de Meta. */
    getPagoConfigPhylax: build.query<PagoConfigPhylax, void>({
      query: () => '/marketplace/phylax/pago-config',
      transformResponse: (res: { data: PagoConfigPhylax }) => res.data,
    }),
    /** Cobra (o cambia) el plan con el token de Culqi.js. */
    contratarPhylax: build.mutation<{ ok: boolean; cambio: boolean }, { planKey: string; token: string }>({
      query: (body) => ({ url: '/marketplace/phylax/contratar', method: 'POST', body }),
      transformResponse: (res: { data: { ok: boolean; cambio: boolean } }) => res.data,
      invalidatesTags: ['PhylaxPlan'],
    }),
    recargarPhylax: build.mutation<{ ok: boolean; saldoUsd: number; mensajesQueAlcanzan: number }, { token: string; montoUsd: number }>({
      query: (body) => ({ url: '/marketplace/phylax/recargar', method: 'POST', body }),
      transformResponse: (res: { data: { ok: boolean; saldoUsd: number; mensajesQueAlcanzan: number } }) => res.data,
      invalidatesTags: ['PhylaxPlan'],
    }),
    /** Cómo quedó la configuración del Agente: WhatsApp, Instagram/Facebook, conocimiento. */
    getConfiguracionPhylax: build.query<ConfiguracionPhylax, void>({
      query: () => '/marketplace/phylax/configuracion',
      transformResponse: (res: { data: ConfiguracionPhylax }) => res.data,
      providesTags: ['PhylaxConfig'],
    }),
    conectarWhatsappPhylax: build.mutation<unknown, { code: string; modo: 'api' | 'coexistencia'; waba_id?: string; phone_number_id?: string }>({
      query: (body) => ({ url: '/marketplace/phylax/whatsapp', method: 'POST', body }),
      invalidatesTags: ['PhylaxConfig', 'PhylaxPlan'],
    }),
    urlDeRedPhylax: build.mutation<{ url: string }, { red: 'instagram' | 'facebook'; returnUrl: string }>({
      query: ({ red, returnUrl }) => ({ url: `/marketplace/phylax/redes/${red}/url`, params: { returnUrl } }),
      transformResponse: (res: { data: { url: string } }) => res.data,
    }),
    elegirPaginaPhylax: build.mutation<unknown, { paginaId: string }>({
      query: (body) => ({ url: '/marketplace/phylax/redes/facebook/pagina', method: 'PUT', body }),
      invalidatesTags: ['PhylaxConfig'],
    }),
    desconectarRedPhylax: build.mutation<unknown, { red: 'instagram' | 'facebook' }>({
      query: ({ red }) => ({ url: `/marketplace/phylax/redes/${red}`, method: 'DELETE' }),
      invalidatesTags: ['PhylaxConfig'],
    }),
    generarConocimientoPhylax: build.mutation<unknown, { url: string }>({
      query: (body) => ({ url: '/marketplace/phylax/conocimiento/generar', method: 'POST', body }),
      invalidatesTags: ['PhylaxConfig'],
    }),
    confirmarConocimientoPhylax: build.mutation<unknown, void>({
      query: () => ({ url: '/marketplace/phylax/conocimiento/confirmar', method: 'POST', body: {} }),
      invalidatesTags: ['PhylaxConfig', 'PhylaxPlan'],
    }),

    applyFeatured: build.mutation<ApplyFeaturedResponse, { propertyId: string; days?: number }>({
      query: (body) => ({ url: '/marketplace/featured/apply', method: 'POST', body }),
      transformResponse: (res: { data: ApplyFeaturedResponse }) => res.data,
      invalidatesTags: ['MarketplaceSlots', 'Property'],
    }),

    payOrderWithDlocal: build.mutation<PayWithDlocalResponse, { orderId: string; successUrl: string; failureUrl: string }>({
      query: ({ orderId, successUrl, failureUrl }) => ({
        url:    `/marketplace/orders/${orderId}/pay-with-dlocal`,
        method: 'POST',
        body:   { successUrl, failureUrl },
      }),
      transformResponse: (res: { data: PayWithDlocalResponse }) => res.data,
      invalidatesTags: ['MarketplaceOrder', 'Credits', 'MarketplaceSlots', 'Wallet', 'MarketplaceProduct', 'StudioOverview'],
    }),

    /** Pago one-click usando una tarjeta guardada (chargeRecurring). */
    payOrderWithSavedCard: build.mutation<
      { paymentId: string; status: string; redirectUrl?: string; totalAmount: number; feeAmount: number; currency: string },
      { orderId: string; savedCardId: string }
    >({
      query: ({ orderId, savedCardId }) => ({
        url:    `/marketplace/orders/${orderId}/pay-with-saved-card`,
        method: 'POST',
        body:   { savedCardId },
      }),
      transformResponse: (res: { data: { paymentId: string; status: string; redirectUrl?: string; totalAmount: number; feeAmount: number; currency: string } }) => res.data,
      invalidatesTags: ['MarketplaceOrder', 'Wallet', 'Credits', 'MarketplaceSlots', 'MarketplaceProduct', 'StudioOverview'],
    }),

    /** GET de una orden mía — usado para polling tras 3DS. */
    getMyOrder: build.query<MarketplaceOrder, string>({
      query: (id) => `/marketplace/orders/${id}`,
      transformResponse: (res: { data: MarketplaceOrder }) => res.data,
      providesTags: (_, __, id) => [{ type: 'MarketplaceOrder', id }],
    }),

    listMyOrders: build.query<OrdersResponse, { limit?: number; skip?: number } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.limit) p.set('limit', String(f.limit));
        if (f?.skip)  p.set('skip',  String(f.skip));
        const qs = p.toString();
        return `/marketplace/orders/me${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: OrdersResponse }) => res.data,
      providesTags: ['MarketplaceOrder'],
    }),

    // Admin
    adminListMarketplaceProducts: build.query<MarketplaceProduct[], void>({
      query: () => '/marketplace/admin/products',
      transformResponse: (res: { data: MarketplaceProduct[] }) => res.data,
      providesTags: ['MarketplaceProduct'],
    }),

    adminCreateProduct: build.mutation<MarketplaceProduct, AdminProductPayload>({
      query: (body) => ({ url: '/marketplace/admin/products', method: 'POST', body }),
      transformResponse: (res: { data: MarketplaceProduct }) => res.data,
      invalidatesTags: ['MarketplaceProduct'],
    }),

    adminUpdateProduct: build.mutation<MarketplaceProduct, { id: string; body: Partial<AdminProductPayload> }>({
      query: ({ id, body }) => ({ url: `/marketplace/admin/products/${id}`, method: 'PUT', body }),
      transformResponse: (res: { data: MarketplaceProduct }) => res.data,
      invalidatesTags: ['MarketplaceProduct'],
    }),

    adminDeleteProduct: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/marketplace/admin/products/${id}`, method: 'DELETE' }),
      transformResponse: (res: { data: { ok: boolean } }) => res.data,
      invalidatesTags: ['MarketplaceProduct'],
    }),

    adminGetRevenue: build.query<RevenueResponse, { from?: string; to?: string } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.from) p.set('from', f.from);
        if (f?.to)   p.set('to',   f.to);
        const qs = p.toString();
        return `/marketplace/admin/revenue${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: RevenueResponse }) => res.data,
    }),

    adminListOrders: build.query<OrdersResponse, { userId?: string; kind?: string; status?: string; from?: string; to?: string; limit?: number; skip?: number } | void>({
      query: (f) => {
        const p = new URLSearchParams();
        if (f?.userId) p.set('userId', f.userId);
        if (f?.kind)   p.set('kind',   f.kind);
        if (f?.status) p.set('status', f.status);
        if (f?.from)   p.set('from',   f.from);
        if (f?.to)     p.set('to',     f.to);
        if (f?.limit)  p.set('limit',  String(f.limit));
        if (f?.skip)   p.set('skip',   String(f.skip));
        const qs = p.toString();
        return `/marketplace/admin/orders${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: { data: OrdersResponse }) => res.data,
      providesTags: ['MarketplaceOrder'],
    }),
  }),
});

export interface PayWithDlocalResponse {
  /** ID del PaymentTransaction local (se usa para confirmar luego con el cardToken). */
  paymentId:             string;
  /** Token del checkout en dLocal Go — el frontend lo pasa al SDK SmartFields. */
  merchantCheckoutToken: string;
  totalAmount:           number;
  feeAmount:             number;
  currency:              string;
}

export const {
  useListMarketplaceProductsQuery,
  useLazyEvaluarCuponQuery,
  usePurchaseProductMutation,
  useGetMiPlanPhylaxQuery,
  useGetTienePhylaxQuery,
  useGetPagoConfigPhylaxQuery,
  useLazyGetPagoConfigPhylaxQuery,
  useContratarPhylaxMutation,
  useRecargarPhylaxMutation,
  useGetConfiguracionPhylaxQuery,
  useConectarWhatsappPhylaxMutation,
  useUrlDeRedPhylaxMutation,
  useElegirPaginaPhylaxMutation,
  useDesconectarRedPhylaxMutation,
  useGenerarConocimientoPhylaxMutation,
  useConfirmarConocimientoPhylaxMutation,
  usePayOrderWithDlocalMutation,
  usePayOrderWithSavedCardMutation,
  useApplyFeaturedMutation,
  useListMyOrdersQuery,
  useLazyGetMyOrderQuery,
  useAdminListMarketplaceProductsQuery,
  useAdminCreateProductMutation,
  useAdminUpdateProductMutation,
  useAdminDeleteProductMutation,
  useAdminGetRevenueQuery,
  useAdminListOrdersQuery,
} = marketplaceApi;
